import React, { useEffect, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    Play, Pause, SkipBack, SkipForward, RotateCcw, Volume2, VolumeX, Download,
    Building2, Signal, Landmark, ShoppingBag, HeartPulse, ShieldCheck, Home,
    GraduationCap, Plane, Car, Truck, UtensilsCrossed, Clapperboard, Factory, Sparkles,
} from "lucide-react";

/* ---- cinematic AI palette ---- */
const C = {
    bg: "#08060F", bg2: "#140A24",
    violet: "#8B5CF6", violetLt: "#A78BFA",
    cyan: "#22D3EE", cyanLt: "#67E8F9",
    cream: "#ECEAF6", ink: "#06040D",
};
const easeOut = [0.22, 1, 0.36, 1];

/* ---- brand configs ---- */
export const BRAND_FUNDLE = {
    key: "fundle", name: "Fundle",
    logoImg: "/movie/fundle-logo.png",
    vo: "/vo", shots: "/movie",
    proposal: "/AI-Agent-Platform-Proposal.pdf", demo: "/demo",
};
export const BRAND_XCEED = {
    key: "xceed", name: "XceedAgents",
    wordmark: { a: "Xceed", b: "Agents" },
    vo: "/vo-xceed", shots: "/movie-xceed",
    proposal: "/XceedAgents-Proposal.pdf", demo: "/demo",
};

const AI = "/movie"; // shared, brand-neutral AI-world imagery

function buildScenes(brand) {
    const s = brand.shots;
    return [
        { id: 1, kind: "hero", bg: `${AI}/world-hero.jpg`, kicker: "The Agentic Era", title: "The world is going agentic.",
          caption: "Across every industry, AI agents are being built and deployed — no longer just answering questions, but taking action, making decisions, and getting real work done." },
        { id: 2, kind: "montage", kicker: "Agents, Everywhere", title: "Omnipresent, at the front of every business.",
          caption: "They're on websites and mobile apps, across social and messaging, and inside the contact centre — running entire workflows. Every business is racing to put an intelligent agent at the front of every interaction.",
          cards: [
              { src: `${AI}/agents-web.jpg`, frame: "browser", label: "On your website", rot: -4 },
              { src: `${AI}/agents-social.jpg`, frame: "phone", label: "On social channels", rot: 3 },
              { src: `${AI}/agents-messaging.jpg`, frame: "phone", label: "In messaging apps", rot: -3 },
              { src: `${AI}/agents-mobile.jpg`, frame: "phone", label: "In your mobile app", rot: 4 },
              { src: `${AI}/agents-dashboard.jpg`, frame: "browser", label: "Across workflows", rot: 5 },
              { src: `${AI}/agents-callcenter.jpg`, frame: "browser", label: "In the contact centre", rot: -5 },
          ] },
        { id: 3, kind: "brand", kicker: "Introducing", title: `Meet ${brand.name}.`,
          caption: `That's where ${brand.name} comes in. ${brand.name} takes over all your front-end communication — and connects straight into your back-end systems.` },
        { id: 4, kind: "shot", frame: "phone", imgs: [`${s}/widget-answer.png`], kicker: "The Front-End Experience", title: "A branded assistant with a name, a face, a personality.",
          caption: "On the front end, your customers meet a branded assistant that answers instantly — grounded only in your approved content, so it never makes things up." },
        { id: 5, kind: "shot", frame: "phone", imgs: [`${s}/widget-booking.png`], kicker: "Guided Journeys & Lead Capture", title: "It doesn't just answer — it gets things done.",
          caption: `${brand.name} guides visitors through real tasks like bookings and enquiries, capturing every detail into structured leads your team can act on within minutes.` },
        { id: 6, kind: "shot", frame: "browser", imgs: [`${s}/deploy.png`], kicker: "Integration & Deployment", title: "One line of code. Every channel.",
          caption: `A single line of code embeds ${brand.name} on any website. Share it as a link, add it to your social channels, or roll it out across your entire organisation.` },
        { id: 7, kind: "shot", frame: "browser", imgs: [`${s}/dashboard.png`, `${s}/analytics.png`], kicker: "The Command Centre", title: "See everything. Control everything.",
          caption: "From one elegant dashboard you monitor every conversation, lead, and support ticket — with analytics that reveal what your customers really want." },
        { id: 8, kind: "shot", frame: "browser", imgs: [`${s}/documents.png`], kicker: "Training — Do It Yourself", title: "Your knowledge becomes its brain.",
          caption: `Upload documents, add question-and-answer pairs, or point it at your website. Even scanned files are read automatically — making ${brand.name} intelligent is do-it-yourself.` },
        { id: 9, kind: "shot", frame: "browser", imgs: [`${s}/train.png`, `${s}/playground.png`], kicker: "Train & Rehearse", title: "Perfect every answer before it goes live.",
          caption: "Fine-tune its personality, tone, and rules, then rehearse every response in a live playground before a single customer sees it. You are always in control." },
        { id: 10, kind: "diagram", kicker: "APIs & Every Industry", title: "One platform. Every industry.",
          caption: `Through secure APIs, ${brand.name} connects to your internal systems. From hotels to healthcare, banking to logistics, retail to real estate — for any industry it delivers the exact answers people need.` },
        { id: 11, kind: "close", kicker: "Ready for the world", title: "Build it. Train it. Deploy it.",
          caption: `${brand.name} — the AI agent for the front of your business, connected to the back. Let's build yours.` },
    ];
}

const INDUSTRIES = [
    { icon: Building2, label: "Hotels & Hospitality", use: "Bookings & dining" },
    { icon: Signal, label: "Telecom & Utilities", use: "Bills & plans" },
    { icon: Landmark, label: "Banking & Finance", use: "Statements & KYC" },
    { icon: ShoppingBag, label: "Retail & E-commerce", use: "Orders & returns" },
    { icon: HeartPulse, label: "Healthcare", use: "Appointments" },
    { icon: ShieldCheck, label: "Insurance", use: "Claims & renewals" },
    { icon: Home, label: "Real Estate", use: "Listings & visits" },
    { icon: GraduationCap, label: "Education", use: "Admissions" },
    { icon: Plane, label: "Travel & Airlines", use: "Tickets & check-in" },
    { icon: Car, label: "Automotive", use: "Test drives & service" },
    { icon: Truck, label: "Logistics", use: "Tracking & pickups" },
    { icon: UtensilsCrossed, label: "Restaurants & QSR", use: "Orders & tables" },
    { icon: Clapperboard, label: "Media & OTT", use: "Subscriptions" },
    { icon: Factory, label: "Manufacturing", use: "Orders & support" },
];

/* ---- brand mark (image logo OR styled wordmark) ---- */
function BrandMark({ brand, height }) {
    if (brand.logoImg) return <img src={brand.logoImg} alt={brand.name} style={{ height }} />;
    return (
        <span style={{ fontFamily: "Manrope, sans-serif", fontWeight: 800, fontSize: height, lineHeight: 1, letterSpacing: "-0.02em", color: C.cream, whiteSpace: "nowrap", display: "inline-block" }}>
            {brand.wordmark.a}<span style={{ color: C.cyan }}>{brand.wordmark.b}</span>
        </span>
    );
}

/* ---- animated neural-network background ---- */
function AIBackground() {
    const canvasRef = useRef(null);
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");
        let raf, w, h, dpr, nodes;
        const init = () => {
            dpr = Math.min(window.devicePixelRatio || 1, 2);
            w = canvas.clientWidth; h = canvas.clientHeight;
            canvas.width = w * dpr; canvas.height = h * dpr;
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
            const count = Math.min(70, Math.floor((w * h) / 22000));
            nodes = Array.from({ length: count }, () => ({
                x: Math.random() * w, y: Math.random() * h,
                vx: (Math.random() - 0.5) * 0.25, vy: (Math.random() - 0.5) * 0.25,
            }));
        };
        const draw = () => {
            ctx.clearRect(0, 0, w, h);
            for (const n of nodes) {
                n.x += n.vx; n.y += n.vy;
                if (n.x < 0 || n.x > w) n.vx *= -1;
                if (n.y < 0 || n.y > h) n.vy *= -1;
            }
            for (let i = 0; i < nodes.length; i++)
                for (let j = i + 1; j < nodes.length; j++) {
                    const a = nodes[i], b = nodes[j];
                    const d = Math.hypot(a.x - b.x, a.y - b.y);
                    if (d < 150) {
                        ctx.strokeStyle = `rgba(139,92,246,${(1 - d / 150) * 0.22})`;
                        ctx.lineWidth = 1;
                        ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke();
                    }
                }
            for (const n of nodes) {
                ctx.fillStyle = "rgba(34,211,238,0.65)";
                ctx.beginPath(); ctx.arc(n.x, n.y, 1.6, 0, Math.PI * 2); ctx.fill();
            }
            raf = requestAnimationFrame(draw);
        };
        init(); draw();
        const onResize = () => init();
        window.addEventListener("resize", onResize);
        return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", onResize); };
    }, []);
    return (
        <>
            <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" style={{ opacity: 0.5 }} />
            <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(760px 520px at 78% -8%, rgba(139,92,246,0.28), transparent 70%), radial-gradient(640px 480px at 12% 108%, rgba(34,211,238,0.18), transparent 70%)" }} />
        </>
    );
}

function BrowserFrame({ children, url = "app.ai-agent.io" }) {
    return (
        <div className="rounded-xl overflow-hidden w-full" style={{ border: "1px solid rgba(103,232,249,0.25)", boxShadow: "0 40px 120px -20px rgba(0,0,0,0.75)", background: "#fff" }}>
            <div className="flex items-center gap-2 px-4 py-2.5" style={{ background: "#1b1530", borderBottom: "1px solid #2a2145" }}>
                <span className="w-3 h-3 rounded-full" style={{ background: "#e06c5f" }} />
                <span className="w-3 h-3 rounded-full" style={{ background: "#e0b34f" }} />
                <span className="w-3 h-3 rounded-full" style={{ background: "#6fae72" }} />
                <div className="ml-3 flex-1 rounded-md text-[11px] px-3 py-1 truncate" style={{ background: "rgba(255,255,255,0.08)", color: C.cyanLt }}>{url}</div>
            </div>
            {children}
        </div>
    );
}
function PhoneFrame({ children }) {
    return (
        <div className="rounded-[2rem] overflow-hidden mx-auto" style={{ width: "min(300px, 72vw)", border: "10px solid #140d22", boxShadow: "0 40px 120px -20px rgba(0,0,0,0.8)", background: "#fff" }}>{children}</div>
    );
}
function KenBurns({ src, delay = 0, z = 1, maxScale = 1.14 }) {
    return (
        <motion.img src={src} alt="" className="w-full h-full object-cover object-top absolute inset-0"
            initial={{ scale: 1.02, opacity: delay ? 0 : 1 }} animate={{ scale: maxScale, opacity: 1 }}
            transition={{ scale: { duration: 18, ease: "linear" }, opacity: { duration: 1.4, delay, ease: "easeInOut" } }} style={{ zIndex: z }} />
    );
}
function TitleWords({ text, highlight = [] }) {
    return (
        <h1 className="font-heading leading-[1.05]" style={{ color: C.cream, fontSize: "clamp(2.3rem,6vw,4.8rem)" }}>
            {text.split(" ").map((word, i) => (
                <motion.span key={i} className="inline-block mr-[0.25em]"
                    initial={{ opacity: 0, y: 28, filter: "blur(6px)" }} animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                    transition={{ duration: 0.6, delay: 0.2 + i * 0.09, ease: easeOut }}
                    style={{ color: highlight.includes(i) ? C.cyan : C.cream }}>{word}</motion.span>
            ))}
        </h1>
    );
}
function MiniFrame({ card }) {
    const Inner = card.frame === "phone" ? (
        <div className="rounded-2xl overflow-hidden" style={{ border: "5px solid #140d22", width: 116 }}>
            <img src={card.src} alt="" className="w-full block" />
        </div>
    ) : (
        <div className="rounded-lg overflow-hidden" style={{ border: "1px solid rgba(103,232,249,0.3)", width: 234 }}>
            <div className="flex gap-1.5 px-2.5 py-1.5" style={{ background: "#1b1530" }}>
                <span className="w-2 h-2 rounded-full" style={{ background: "#e06c5f" }} />
                <span className="w-2 h-2 rounded-full" style={{ background: "#e0b34f" }} />
                <span className="w-2 h-2 rounded-full" style={{ background: "#6fae72" }} />
            </div>
            <img src={card.src} alt="" className="w-full block" />
        </div>
    );
    return (
        <div className="flex flex-col items-center gap-2">
            <div style={{ boxShadow: "0 26px 70px -24px rgba(0,0,0,0.8)", borderRadius: 12 }}>{Inner}</div>
            <span className="text-[10px] tracking-[0.22em] uppercase" style={{ color: C.cyanLt }}>{card.label}</span>
        </div>
    );
}

function Stage({ scene, brand }) {
    if (scene.kind === "hero") {
        return (
            <>
                <motion.img src={scene.bg} alt="" className="fixed inset-0 w-full h-full object-cover"
                    initial={{ scale: 1.08, opacity: 0 }} animate={{ scale: 1.16, opacity: 0.55 }}
                    transition={{ opacity: { duration: 1.5 }, scale: { duration: 20, ease: "linear" } }} />
                <div className="fixed inset-0" style={{ background: "linear-gradient(180deg, rgba(8,6,15,0.55), rgba(8,6,15,0.86))" }} />
                <div className="relative text-center px-6 max-w-4xl">
                    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: easeOut }}
                        className="inline-flex items-center gap-2 mb-8 px-4 py-1.5 rounded-full text-[11px] tracking-[0.35em] uppercase" style={{ color: C.cyan, border: "1px solid rgba(34,211,238,0.4)" }}>
                        <Sparkles size={13} /> {scene.kicker}
                    </motion.div>
                    <TitleWords text={scene.title} highlight={[3]} />
                </div>
            </>
        );
    }
    if (scene.kind === "montage") {
        return (
            <div className="w-full max-w-6xl px-6">
                <div className="flex items-end justify-center gap-5 md:gap-7 flex-wrap">
                    {scene.cards.map((card, i) => (
                        <motion.div key={card.src}
                            initial={{ opacity: 0, y: 50, rotate: 0 }}
                            animate={{ opacity: 1, y: [0, -10, 0], rotate: card.rot }}
                            transition={{ opacity: { duration: 0.5, delay: 0.2 + i * 0.14 }, rotate: { duration: 0.7, delay: 0.2 + i * 0.14, ease: easeOut }, y: { duration: 6, repeat: Infinity, ease: "easeInOut", delay: i * 0.3 } }}>
                            <MiniFrame card={card} />
                        </motion.div>
                    ))}
                </div>
            </div>
        );
    }
    if (scene.kind === "brand") {
        return (
            <div className="text-center px-6">
                <motion.div initial={{ opacity: 0, scale: 0.85 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, ease: easeOut }} className="relative inline-block">
                    <motion.div className="absolute rounded-full" style={{ inset: "-60px", background: "radial-gradient(circle, rgba(139,92,246,0.45), transparent 65%)" }} animate={{ opacity: [0.4, 0.8, 0.4] }} transition={{ duration: 3, repeat: Infinity }} />
                    <div className="relative"><BrandMark brand={brand} height="clamp(58px,10vw,110px)" /></div>
                </motion.div>
                <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.7 }} className="mt-10 font-heading" style={{ color: C.cream, fontSize: "clamp(1.4rem,3.2vw,2.4rem)" }}>
                    The AI agent for the <span style={{ color: C.cyan }}>front</span> of your business —
                    <br className="hidden md:block" /> connected to the <span style={{ color: C.violetLt }}>back</span>.
                </motion.p>
            </div>
        );
    }
    if (scene.kind === "close") {
        return (
            <div className="text-center px-6 max-w-4xl">
                <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="mb-10 flex justify-center">
                    <BrandMark brand={brand} height="clamp(44px,7.5vw,76px)" />
                </motion.div>
                <TitleWords text={scene.title} highlight={[0, 2, 4]} />
                <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.1, duration: 0.8 }} className="mt-6 text-lg" style={{ color: "rgba(236,234,246,0.65)" }}>
                    The AI agent for the front of your business, connected to the back.
                </motion.p>
                <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.4, duration: 0.6 }} className="mt-10 flex items-center justify-center gap-4 flex-wrap">
                    <a href={brand.proposal} target="_blank" rel="noreferrer" data-testid="close-download-proposal" className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-medium transition-transform hover:scale-105" style={{ background: `linear-gradient(90deg, ${C.violet}, ${C.cyan})`, color: C.ink }}>
                        <Download size={17} /> Download Proposal
                    </a>
                    <a href={brand.demo} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-medium" style={{ border: "1px solid rgba(236,234,246,0.4)", color: C.cream }}>
                        Try the live agent
                    </a>
                </motion.div>
            </div>
        );
    }
    if (scene.kind === "diagram") {
        return (
            <div className="w-full max-w-6xl px-6">
                <motion.div initial={{ opacity: 0, scale: 0.85 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, ease: easeOut }} className="mx-auto mb-8 w-fit relative">
                    <div className="relative z-10 flex items-center gap-3 px-8 py-4 rounded-2xl" style={{ background: `linear-gradient(135deg, ${C.violet}, ${C.cyan})`, boxShadow: `0 0 60px -6px ${C.violet}` }}>
                        <BrandMark brand={brand} height={26} />
                    </div>
                    <motion.div className="absolute inset-0 rounded-2xl" style={{ border: `1px solid ${C.cyan}` }} animate={{ scale: [1, 1.3], opacity: [0.6, 0] }} transition={{ duration: 2.4, repeat: Infinity, ease: "easeOut" }} />
                </motion.div>
                <div className="text-center mb-5 text-[11px] tracking-[0.3em] uppercase" style={{ color: C.cyan }}>Secure APIs ↕ Your internal systems</div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3">
                    {INDUSTRIES.map((it, i) => {
                        const Icon = it.icon;
                        return (
                            <motion.div key={it.label} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.3 + i * 0.06, ease: easeOut }}
                                className="rounded-xl p-3 text-center" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(103,232,249,0.2)", backdropFilter: "blur(8px)" }}>
                                <div className="w-9 h-9 rounded-lg mx-auto mb-2 flex items-center justify-center" style={{ background: "rgba(34,211,238,0.12)", color: C.cyanLt }}>
                                    <Icon size={17} />
                                </div>
                                <div className="font-heading text-[13px] leading-tight" style={{ color: C.cream }}>{it.label}</div>
                                <div className="text-[10px] mt-0.5" style={{ color: "rgba(236,234,246,0.5)" }}>{it.use}</div>
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        );
    }
    /* shot */
    const Frame = scene.frame === "phone" ? PhoneFrame : BrowserFrame;
    const isPhone = scene.frame === "phone";
    const ratio = isPhone ? "216%" : "62.5%";
    const url = `app.${brand.name.toLowerCase()}.io`;
    return (
        <div className="w-full flex flex-col items-center px-6">
            <motion.div initial={{ opacity: 0, y: 40, rotateX: 6 }} animate={{ opacity: 1, y: 0, rotateX: 0 }} transition={{ duration: 0.8, ease: easeOut }}
                style={{ width: isPhone ? "auto" : "min(920px, 92vw)", perspective: 1200 }}>
                <Frame url={url}>
                    <div className="relative w-full overflow-hidden" style={{ paddingBottom: ratio }}>
                        {scene.imgs.map((src, i) => (
                            <KenBurns key={src} src={src} delay={i === 0 ? 0 : 9} z={i + 1} maxScale={isPhone ? 1.06 : 1.14} />
                        ))}
                    </div>
                </Frame>
            </motion.div>
        </div>
    );
}

export default function MoviePlayer({ brand = BRAND_FUNDLE }) {
    const SCENES = React.useMemo(() => buildScenes(brand), [brand]);
    const [started, setStarted] = useState(false);
    const [index, setIndex] = useState(0);
    const [playing, setPlaying] = useState(false);
    const [muted, setMuted] = useState(false);
    const [ended, setEnded] = useState(false);
    const [showChrome, setShowChrome] = useState(true);
    const audioRef = useRef(null);
    const hideTimer = useRef(null);

    const scene = SCENES[index];

    useEffect(() => {
        if (!started) return;
        const a = audioRef.current; if (!a) return;
        a.src = `${brand.vo}/scene-${SCENES[index].id}.mp3`;
        a.muted = muted;
        if (playing) a.play().catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [index, started]);

    useEffect(() => {
        const a = audioRef.current; if (!a || !started) return;
        if (playing) a.play().catch(() => {}); else a.pause();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [playing]);

    useEffect(() => { if (audioRef.current) audioRef.current.muted = muted; }, [muted]);

    const handleEnded = () => {
        setIndex((prev) => {
            if (prev < SCENES.length - 1) return prev + 1;
            setPlaying(false); setEnded(true); return prev;
        });
    };

    const revealChrome = useCallback(() => {
        setShowChrome(true);
        if (hideTimer.current) clearTimeout(hideTimer.current);
        hideTimer.current = setTimeout(() => setShowChrome(false), 3500);
    }, []);
    useEffect(() => { revealChrome(); return () => hideTimer.current && clearTimeout(hideTimer.current); }, [index, revealChrome]);

    const start = () => { setStarted(true); setEnded(false); setIndex(0); setPlaying(true); };
    const replay = () => { setEnded(false); setIndex(0); setPlaying(true); };
    const go = (i) => { setEnded(false); setIndex(Math.max(0, Math.min(SCENES.length - 1, i))); };

    const chromeStyle = { opacity: showChrome || !playing ? 1 : 0, transition: "opacity 0.5s ease" };

    return (
        <div data-testid="movie-root" onMouseMove={revealChrome} onTouchStart={revealChrome}
            className="fixed inset-0 overflow-hidden select-none"
            style={{ background: `radial-gradient(1200px 800px at 50% 0%, ${C.bg2}, ${C.bg} 65%)`, fontFamily: "Manrope, sans-serif", cursor: showChrome || !playing ? "auto" : "none" }}>
            <AIBackground />
            <audio ref={audioRef} onEnded={handleEnded} preload="auto" />

            <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-6 md:px-10 py-5 z-30" style={chromeStyle}>
                <BrandMark brand={brand} height={26} />
                <div className="text-[11px] tracking-[0.3em] uppercase" style={{ color: "rgba(236,234,246,0.5)" }}>
                    Product Film · {String(index + 1).padStart(2, "0")} / {String(SCENES.length).padStart(2, "0")}
                </div>
            </div>

            <div className="absolute inset-0 flex items-center justify-center pt-16 pb-40">
                <AnimatePresence mode="wait">
                    <motion.div key={scene.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }} className="w-full flex items-center justify-center relative">
                        <Stage scene={scene} brand={brand} />
                    </motion.div>
                </AnimatePresence>
            </div>

            <div className="absolute left-0 right-0 bottom-28 md:bottom-24 px-6 z-20">
                <AnimatePresence mode="wait">
                    <motion.div key={`cap-${scene.id}`} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.5, delay: 0.15 }} className="max-w-3xl mx-auto text-center">
                        {scene.kind !== "hero" && scene.kind !== "brand" && scene.kind !== "close" && (
                            <div className="text-[11px] tracking-[0.35em] uppercase mb-2" style={{ color: C.cyan }}>{scene.kicker}</div>
                        )}
                        <p className="text-sm md:text-base leading-relaxed" style={{ color: "rgba(236,234,246,0.82)" }}>{scene.caption}</p>
                    </motion.div>
                </AnimatePresence>
            </div>

            <div className="absolute left-0 right-0 bottom-0 z-30 px-6 md:px-10 pb-6 pt-3 flex items-center justify-between" style={chromeStyle}>
                <div className="flex items-center gap-2">
                    <button onClick={() => go(index - 1)} data-testid="movie-prev" className="w-10 h-10 rounded-full flex items-center justify-center" style={{ color: C.cream, border: "1px solid rgba(236,234,246,0.2)" }}><SkipBack size={17} /></button>
                    <button onClick={() => setPlaying((p) => !p)} data-testid="movie-playpause" className="w-12 h-12 rounded-full flex items-center justify-center transition-transform hover:scale-105" style={{ background: `linear-gradient(90deg, ${C.violet}, ${C.cyan})`, color: C.ink }}>{playing ? <Pause size={20} /> : <Play size={20} className="ml-0.5" />}</button>
                    <button onClick={() => go(index + 1)} data-testid="movie-next" className="w-10 h-10 rounded-full flex items-center justify-center" style={{ color: C.cream, border: "1px solid rgba(236,234,246,0.2)" }}><SkipForward size={17} /></button>
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={replay} data-testid="movie-replay" className="w-10 h-10 rounded-full flex items-center justify-center" style={{ color: C.cream, border: "1px solid rgba(236,234,246,0.2)" }}><RotateCcw size={16} /></button>
                    <button onClick={() => setMuted((m) => !m)} data-testid="movie-mute" className="w-10 h-10 rounded-full flex items-center justify-center" style={{ color: C.cream, border: "1px solid rgba(236,234,246,0.2)" }}>{muted ? <VolumeX size={17} /> : <Volume2 size={17} />}</button>
                    <a href={brand.proposal} target="_blank" rel="noreferrer" data-testid="movie-proposal-link" className="hidden md:inline-flex items-center gap-2 px-4 h-10 rounded-full text-sm" style={{ color: C.ink, background: C.cream }}><Download size={15} /> Proposal PDF</a>
                </div>
            </div>

            <AnimatePresence>
                {(!started || ended) && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 z-40 flex flex-col items-center justify-center text-center px-6" style={{ background: "rgba(6,4,13,0.9)", backdropFilter: "blur(6px)" }} data-testid="movie-overlay">
                        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: easeOut }}>
                            <div className="mb-8 flex justify-center"><BrandMark brand={brand} height="clamp(44px,8vw,84px)" /></div>
                            <div className="inline-flex items-center gap-2 mb-6 px-4 py-1.5 rounded-full text-[11px] tracking-[0.35em] uppercase" style={{ color: C.cyan, border: "1px solid rgba(34,211,238,0.4)" }}><Sparkles size={13} /> Product Film</div>
                            <h1 className="font-heading mb-4" style={{ color: C.cream, fontSize: "clamp(2.2rem,5.5vw,4.2rem)", lineHeight: 1.05 }}>The AI agent for<br /> the front of your business.</h1>
                            <p className="max-w-xl mx-auto mb-9 text-base" style={{ color: "rgba(236,234,246,0.7)" }}>
                                {ended ? "That's the film. Replay it, download the full proposal, or try the live agent." : `A short film on the agentic AI era — and how ${brand.name} takes over your front-end communication and connects to your back-end systems.`}
                            </p>
                            <div className="flex items-center justify-center gap-4 flex-wrap">
                                <button onClick={ended ? replay : start} data-testid="movie-start" className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full font-medium text-lg transition-transform hover:scale-105" style={{ background: `linear-gradient(90deg, ${C.violet}, ${C.cyan})`, color: C.ink }}>
                                    {ended ? <RotateCcw size={19} /> : <Play size={19} />} {ended ? "Replay film" : "Play film"}
                                </button>
                                <a href={brand.proposal} target="_blank" rel="noreferrer" data-testid="movie-overlay-proposal" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-medium text-lg" style={{ border: "1px solid rgba(236,234,246,0.35)", color: C.cream }}><Download size={18} /> Proposal PDF</a>
                            </div>
                            <p className="mt-6 text-xs" style={{ color: "rgba(236,234,246,0.4)" }}>Best with sound on · Professional voiceover included</p>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
